/**
 * Skills Tracker - Student Pathways GPS
 * Copying Khan Academy's skill mastery system
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, TrendingUp, Target, Star, Plus,
  CheckCircle, Zap, Brain, Code, Users,
  MessageSquare, Lightbulb, Briefcase, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import Navigation from '@/components/Navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Skill categories
const SKILL_CATEGORIES = [
  {
    id: 'communication',
    name: 'Communication',
    icon: MessageSquare,
    skills: [
      { id: 'public_speaking', name: 'Public Speaking', level: 0 },
      { id: 'writing', name: 'Professional Writing', level: 0 },
      { id: 'active_listening', name: 'Active Listening', level: 0 },
      { id: 'presentation', name: 'Presentations', level: 0 }
    ]
  },
  {
    id: 'technical',
    name: 'Technical',
    icon: Code,
    skills: [
      { id: 'computer_basics', name: 'Computer Basics', level: 0 },
      { id: 'microsoft_office', name: 'Microsoft Office', level: 0 },
      { id: 'social_media', name: 'Social Media', level: 0 },
      { id: 'data_entry', name: 'Data Entry', level: 0 }
    ]
  },
  {
    id: 'professional',
    name: 'Professional',
    icon: Briefcase,
    skills: [
      { id: 'time_management', name: 'Time Management', level: 0 },
      { id: 'teamwork', name: 'Teamwork', level: 0 },
      { id: 'problem_solving', name: 'Problem Solving', level: 0 },
      { id: 'leadership', name: 'Leadership', level: 0 }
    ]
  },
  {
    id: 'personal',
    name: 'Personal',
    icon: Lightbulb,
    skills: [
      { id: 'adaptability', name: 'Adaptability', level: 0 },
      { id: 'critical_thinking', name: 'Critical Thinking', level: 0 },
      { id: 'creativity', name: 'Creativity', level: 0 },
      { id: 'self_motivation', name: 'Self-Motivation', level: 0 }
    ]
  }
];

const SkillsTracker = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSkills();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSkills = async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile/skills`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSkills(data.skills || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSkillLevel = async (skillId, newLevel) => {
    try {
      const response = await fetch(`${API_URL}/api/profile/skills`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          skill_id: skillId,
          level: newLevel
        })
      });

      if (response.ok) {
        toast.success('Skill level updated! 📈');
        fetchSkills();
      }
    } catch (error) {
      toast.error('Failed to update skill');
    }
  };

  const getSkillLevel = (skillId) => {
    const skill = skills.find(s => s.skill_id === skillId);
    return skill?.level || 0;
  };

  const getLevelColor = (level) => {
    if (level >= 4) return '#22C55E';
    if (level >= 3) return '#3B82F6';
    if (level >= 2) return '#F59E0B';
    if (level >= 1) return '#EF4444';
    return colors.text_muted;
  };

  const getLevelLabel = (level) => {
    if (level >= 4) return 'Expert';
    if (level >= 3) return 'Proficient';
    if (level >= 2) return 'Intermediate';
    if (level >= 1) return 'Beginner';
    return 'Not Started';
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div className="p-6 md:p-8 rounded-b-3xl shadow-lg mb-6" style={{ background: colors.header_gradient || colors.header }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <Award className="w-10 h-10 text-white" />
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white" style={{ fontFamily: 'var(--font-heading)' }}>Skills Tracker</h1>
          </div>
          <p className="text-lg text-white/90">Track your growth and build job-ready skills! 💪</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 space-y-6">
        {SKILL_CATEGORIES.map(category => (
          <Card key={category.id} className="p-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}>
                <category.icon className="w-5 h-5" style={{ color: '#3B82F6' }} />
              </div>
              <h3 className="text-xl font-bold" style={{ color: colors.text }}>{category.name} Skills</h3>
            </div>

            <div className="space-y-4">
              {category.skills.map(skill => {
                const currentLevel = getSkillLevel(skill.id);
                const levelColor = getLevelColor(currentLevel);
                
                return (
                  <div key={skill.id} className="p-4 rounded-lg" style={{ backgroundColor: colors.surface }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-bold" style={{ color: colors.text }}>{skill.name}</h4>
                        <p className="text-xs font-semibold mt-1" style={{ color: levelColor }}>
                          {getLevelLabel(currentLevel)}
                        </p>
                      </div>
                      <Badge className="font-semibold" style={{ backgroundColor: `${levelColor}20`, color: levelColor }}>
                        Level {currentLevel}/5
                      </Badge>
                    </div>
                    
                    <Progress value={currentLevel * 20} className="h-2 mb-3" />
                    
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(level => (
                        <Button
                          key={level}
                          size="sm"
                          variant={currentLevel >= level ? 'default' : 'outline'}
                          onClick={() => updateSkillLevel(skill.id, level)}
                          className={`flex-1 font-semibold ${
                            currentLevel >= level ? 'bg-blue-600 text-white' : ''
                          }`}
                          style={{
                            borderColor: colors.border,
                            color: currentLevel >= level ? 'white' : colors.text
                          }}
                        >
                          {level}
                        </Button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      <Navigation user={user} />
    </div>
  );
};

export default SkillsTracker;
